import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  ReactElement,
} from 'react';
import { PopUpHandler } from '../../../shared/PopupHandler';
import { NotificationMessage } from '../../../shared/NotificationMessage';
import { useDispatch, useSelector } from 'react-redux';
import { cashierModal, CashierType } from '../../../../state/cashier';
import { setCashierModal } from '../../../../state/cashier/reducers';

import _PaymentIQCashier, { IPiqCashierApiMethods, IPiqCashierConfig } from 'paymentiq-cashier-bootstrapper';
import { useUserContext } from '../../../../providers/UserProvider';
import { useSnackbar } from '../../../../hooks/snackbar';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchSessionId, claimPromoBonus } from './service';
import { useAuth } from 'react-oidc-context';
import useDestroyUserSession from '../../../../hooks/auth/useDestroyUserSession';
import { trackEvent } from '../../../../tracking/tracking';
import { DepositEvent } from '../../../../tracking/types';
import { APP_URLS, locale, xTenantId } from '../../../../consts';
import { Paragraph } from '../../../shared/Paragraph';
import { Link } from '../../../shared/Link';
import { NavGiftIcon } from '../../../shared/SVG/Icons';
import { AccountTile } from '../AccountTile';
import { Heading } from '../../../shared/Heading';
import { setLoadPlatformPromotions } from '../../../../state/promotions/reducers';
import { loadPlatformPromotions } from '../../../../state/promotions';
import { NativeEventBus } from '../../../../native/NativeEventBus';
import { Spinner } from '../../../shared/Spinner';
import Cookies from 'js-cookie';
import { FirstDepositConsents } from './FirstDepositConsents/FirstDepositConsents';
import './Cashier.scss';
import { useUserTransactionsLazyQuery } from '../../../../libs/graphql/baseAppAPI/queries/__generated__/user-transactions.query.generated';

type TransactionStatus = 'success' | 'failure' | 'pending' | 'cancelled' | null;
const CASHIER_CONTAINER_ID = 'cashier-container';

const NativeStatusWrapper = ({ children }: { children: ReactNode }): ReactElement => {
  return <section className="native-status-wrapper">{children}</section>;
};

interface TransactionSuccessData {
  data: {
    payload: {
      txAmount: string;
      txAmountCy: string;
    };
  };
}

export const Cashier: FunctionComponent = () => {
  const { t } = useTranslations();
  const auth = useAuth();
  const { logoutUser } = useDestroyUserSession();
  const cashierModalStore = useSelector(cashierModal);
  const renewPromotionsState = useSelector(loadPlatformPromotions);
  const dispatch = useDispatch();
  const { type, isOpen, promoCode, amount, promoTag } = cashierModalStore;
  const cashierRef = useRef<HTMLDivElement>(null);
  const { userProfileId, user, compliance } = useUserContext();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatus>(null);
  const { addSnack } = useSnackbar();
  const [isContainerReady, setIsContainerReady] = useState(false);
  const deposit = type === CashierType.Deposit;
  const withdrawal = type === CashierType.Withdrawal;
  const cashierType = deposit ? CashierType.Deposit : withdrawal ? CashierType.Withdrawal : undefined;
  const navigate = useNavigate();
  const [nativeDepositStatus, setNativeDepositStatus] = useState<string | null>(null);
  const nativeEventBus = useMemo(() => new NativeEventBus(), []);
  const [promoStatus, setPromoStatus] = useState<'pending' | 'success' | 'fail'>('pending');
  const [isCashierLoaded, setIsCashierLoaded] = useState(false);
  const location = useLocation();
  const [showPoF, setShowPoF] = useState(false);
  const showCashier = isOpen && !showPoF;
  const [hasPendingTransaction, setHasPendingTransaction] = useState(false);

  const [fetchTransactions] = useUserTransactionsLazyQuery({
    variables: {
      inTenantId: xTenantId,
      inUserProfileId: userProfileId,
      inMediatorType: ['monetary'],
      inOperationType: 'Credit',
    },
    fetchPolicy: 'network-only',
    onCompleted: ({ userTransactions }) => {
      console.log('xyz', userTransactions);
      const transactions = userTransactions?.edges?.map((e) => e?.node) ?? [];
      const hasPending = transactions.some((t) => t?.transactionStatus === 'PendingAuthorization');

      setHasPendingTransaction(hasPending);
    },
  });

  useEffect(() => {
    if (isOpen && userProfileId) fetchTransactions();
  }, [isOpen, userProfileId, fetchTransactions]);

  const isDepositAllowed = compliance?.isDepositAllowed;

  useEffect(() => {
    if (isOpen && !showPoF) {
      (async (): Promise<void> => {
        if (auth.user?.expired) {
          await auth.signinSilent().catch((error) => {
            logoutUser();
          });
        }

        if (!sessionId) {
          getSessionId().then((id) => {
            setSessionId(id);
          });
        }
      })();
    }
  }, [isOpen, sessionId, showPoF]);

  useEffect(() => {
    const eventType = 'deposit-status';

    if (isOpen && deposit) {
      nativeEventBus.listenToEvent(eventType, handleNativeEvent);
    }

    return () => {
      nativeEventBus.removeListener(eventType, handleNativeEvent);
    };
  }, [isOpen, deposit, nativeEventBus]);

  const handleNativeEvent = (event: MessageEvent): void => {
    if (event.data.status) {
      if (event.data.status === 'handling') {
        const iframe = document.getElementById(CASHIER_CONTAINER_ID)?.querySelector('iframe');

        if (iframe) {
          iframe.style.display = 'none';
        }
      }

      setNativeDepositStatus(event.data.status);
    }
  };

  const checkContainerReady = useCallback(() => {
    const ready = Boolean(isOpen && cashierRef.current);

    setIsContainerReady(ready);
  }, [isOpen]);

  useEffect(() => {
    const timeoutId = setTimeout(checkContainerReady, 500);

    return () => clearTimeout(timeoutId);
  }, [isOpen, showCashier, checkContainerReady]);

  useEffect(() => {
    if (isOpen && sessionId && isContainerReady && !transactionStatus && !showPoF) {
      initializeCashier(sessionId);
    }
  }, [isOpen, sessionId, isContainerReady, transactionStatus, showPoF]);

  const getSessionId = async (): Promise<string | null> => {
    try {
      return await fetchSessionId(userProfileId, withdrawal ? 'withdraw' : cashierType);
    } catch (error) {
      addSnack({
        type: 'error',
        message: t('errors.general'),
        theme: 'dark',
      });
      handleClose();

      return null;
    }
  };

  const handlePromoSuccess = async (): Promise<void> => {
    const successful = await claimPromoBonus(userProfileId, promoCode || '', amount || 0, promoTag || '');

    if (successful) {
      dispatch(setLoadPlatformPromotions(!renewPromotionsState));
      setPromoStatus('success');
      navigate('/game-lobby/slots');
    } else {
      setPromoStatus('fail');
    }
  };

  const handleDepositSuccess = (data: TransactionSuccessData): void => {
    const ftdCookie = Cookies.get('ftd');
    const isFTD = ftdCookie === 'true';

    isFTD && Cookies.remove('ftd');

    promoCode && handlePromoSuccess();

    cashierType === 'deposit' &&
      trackEvent<DepositEvent>({
        event: isFTD ? 'firstdepositconfirm' : 'depositconfirm',
        value: Number(data.data.payload.txAmount),
        currency: data.data.payload.txAmountCy,
        playerId: userProfileId,
        playerEmail: String(user?.email),
        playerPhone: String(user?.mobileNumber?.prefix) + user?.mobileNumber?.number,
      });

    setTransactionStatus('success');
  };

  const initializeCashier = (sessionId: string): void => {
    // prefill and disable editing of amount if the cashier has been launched with promo
    const extraConfig =
      promoCode || amount
        ? {
            amount: amount,
            showAmount: false,
          }
        : {};

    try {
      new _PaymentIQCashier(
        '#cashier-container',
        {
          showCookiesWarning: true,
          allowMobilePopup: true,
          fetchConfig: true,
          defaultLoader: true,
          merchantId: process.env.REACT_APP_PAYMENTIQ_MERCHANT_ID as string,
          userId: userProfileId,
          sessionId: sessionId,
          environment: process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION' ? 'production' : 'test',
          method: cashierType as IPiqCashierConfig['method'],
          containerHeight: 'auto',
          locale,
          ...extraConfig,
          trackingPixels: [
            {
              type: 'firstDeposit',
              trackingName: 'FTD tracking pixel',
              imgSrc: `${window.location.origin}/ftd`,
              imgAttrs: `userId=${userProfileId}`,
            },
          ],
        },
        (api: IPiqCashierApiMethods) => {
          api.on({
            success: (data) => {
              // needed to allow tracking, otherwise cashier is destroyed before the request is sent
              setTimeout(() => {
                handleDepositSuccess(data);
              }, 500);
            },
            failure: (data) => {
              setTransactionStatus('failure');
            },
            cancelled: (data) => {
              setTransactionStatus('cancelled');
            },
            pending: (data) => {
              setTransactionStatus('pending');
            },
            isLoading: (data) => {
              console.log('[Cashier] isLoading', data);
            },
            doneLoading: (data) => {
              console.log('[Cashier] doneLoading', data);
              setIsCashierLoaded(true);
            },
            transactionInit: (data) => {
              console.log('[Cashier] transactionInit', data);

              nativeEventBus.transactionInit({
                value: data.data.amount,
                currency: data.data.txAmountCy,
                playerId: userProfileId,
                playerEmail: String(user?.email),
                playerPhone: String(user?.mobileNumber?.prefix) + user?.mobileNumber?.number,
              });
            },
          });
        }
      );
    } catch (error) {
      setTransactionStatus('failure');
    }
  };

  const handleClose = (): void => {
    dispatch(setCashierModal({ isOpen: false, promoCode: undefined }));
    setSessionId(null);
    setTransactionStatus(null);
    setNativeDepositStatus(null);
    setIsContainerReady(false);
    setIsCashierLoaded(false);
    nativeEventBus.closeCashier();
  };

  const handleRetry = (): void => {
    setTransactionStatus(null);
    setNativeDepositStatus(null);
    setSessionId(null);
  };

  const renderContent = (): ReactNode => {
    if (hasPendingTransaction) {
      return (
        <NotificationMessage
          iconVariant="Info"
          className="cashier__info-message"
          text={t(`cashier-deposit-not-available-pending`)}
          buttonText={t('cashier.closeButton')}
          onClick={handleClose}
        />
      );
    }

    if (deposit && isDepositAllowed === false) {
      return (
        <NotificationMessage
          iconVariant="Warning"
          className="cashier__error-message"
          text={
            <>
              {t('cashier-deposit-not-available-restricted')}
              <a href="https://help.betzone.co.uk/support/home" className="customer-service-link">
                {t('cashier-deposit-not-available-restricted-link')}
              </a>{' '}
              {t('cashier-deposit-not-available-restricted-part-2')}
            </>
          }
          buttonText={t('cashier.closeButton')}
          onClick={handleClose}
        />
      );
    }

    if (nativeDepositStatus) {
      // native deposit status is creditable, so we can close the cashier
      if (nativeDepositStatus === 'creditable') {
        return (
          <NativeStatusWrapper>
            <NotificationMessage
              iconVariant="Success"
              className="cashier__success-message"
              text={t(`cashier-deposit-not-available-restricted`)}
              buttonText={t('cashier.closeButton')}
              onClick={handleClose}
            />
          </NativeStatusWrapper>
        );
      }

      // native deposit status is handling by default browser popup
      if (nativeDepositStatus === 'handling') {
        return (
          <NativeStatusWrapper>
            <Spinner theme="dark" />
            <NotificationMessage
              iconVariant="Info"
              className="cashier__info-message"
              text={t('cashier.handling')}
              buttonText={t('cashier.closeButton')}
              onClick={handleClose}
            />
          </NativeStatusWrapper>
        );
      }

      // native deposit status is failed, so we can close the cashier
      if (nativeDepositStatus === 'failed') {
        return (
          <NativeStatusWrapper>
            <NotificationMessage
              iconVariant="Error"
              className="cashier__error-message"
              text={t(`cashier.failed-${cashierType}`)}
              buttonText={t('cashier.tryAgainButton')}
              onClick={handleRetry}
            />
          </NativeStatusWrapper>
        );
      }

      if (nativeDepositStatus === 'expired') {
        return (
          <NativeStatusWrapper>
            <NotificationMessage
              iconVariant="Warning"
              text={t('cashier.expired')}
              className="cashier__error-message"
              buttonText={t('cashier.closeButton')}
              onClick={handleClose}
            />
          </NativeStatusWrapper>
        );
      }
    }

    if (!transactionStatus) {
      return <div id={CASHIER_CONTAINER_ID} className="cashier__content" ref={cashierRef}></div>;
    }

    switch (transactionStatus) {
      case 'success':
        return (
          // if this is a div, the cashier seems to hijack it even without the id
          <section className="success-wrapper">
            <NotificationMessage
              iconVariant="Success"
              className="cashier__success-message"
              text={t(`cashier.success-${cashierType}`)}
              buttonText={t('cashier.closeButton')}
              onClick={handleClose}
            />
            {promoCode &&
              (promoStatus === 'success' ? (
                <AccountTile className="promo-details">
                  <section className="success-heading">
                    <NavGiftIcon active />
                    <Heading level={4}>Bonus awarded!</Heading>
                  </section>
                  <Paragraph size="sm">{`£${amount} has been deposited to your cash account and you have received £${amount} in bonus funds`}</Paragraph>
                </AccountTile>
              ) : promoStatus === 'pending' ? (
                <div className="promo-details">
                  <Paragraph size="sm">Claiming your bonus...</Paragraph>
                </div>
              ) : (
                <div className="promo-details">
                  <Paragraph size="sm">Couldn't claim bonus. Please contact our support.</Paragraph>
                </div>
              ))}
          </section>
        );
      case 'failure':
        return (
          <NotificationMessage
            iconVariant="Error"
            className="cashier__error-message"
            text={t(`cashier.failed-${cashierType}`)}
            buttonText={t('cashier.tryAgainButton')}
            onClick={handleRetry}
          />
        );
      case 'pending':
        return (
          <NotificationMessage
            iconVariant="Info"
            className="cashier__info-message"
            text={t(`cashier.pending-${cashierType}`)}
            buttonText={t('cashier.closeButton')}
            onClick={handleClose}
          />
        );
      case 'cancelled':
        return (
          <NotificationMessage
            iconVariant="Warning"
            text={t(`cashier.cancelled-${cashierType}`)}
            className="cashier__error-message"
            buttonText={t('cashier.tryAgainButton')}
            onClick={handleRetry}
          />
        );
      default:
        return null;
    }
  };

  const navigateWithBackground = useCallback(
    (path: string): void => {
      handleClose();
      navigate(path, {
        state: { previousLocation: location },
      });
    },
    [handleClose, navigate, location]
  );

  return (
    <>
      {showCashier && (
        <PopUpHandler
          isOpen
          header={t(`cashier.${cashierType}.heading`)}
          onClose={handleClose}
          modalProps={{
            'aria-label': type,
            className: 'cashier__modal',
          }}
          drawerProps={{
            'aria-label': type,
            className: 'cashier__drawer',
          }}
        >
          <div className="cashier__shbody">
            {renderContent()}

            {deposit && isCashierLoaded && transactionStatus === null && (
              <div className="cashier__sg">
                <Paragraph size="sm" className="cashier__sg-description">
                  {t('deposit-safer-gambling-description')}
                </Paragraph>
                <div className="cashier__sg-links">
                  <Link
                    onClick={(): void => navigateWithBackground(`${APP_URLS.myAccount.gamblingControls}?edit=monetary`)}
                  >
                    {t('deposit-safer-gambling-link-limits')}
                  </Link>

                  <Link onClick={(): void => navigateWithBackground(APP_URLS.myAccount.gamblingControls)}>
                    {t('deposit-safer-gambling-link-tools')}
                  </Link>
                  <Link
                    onClick={(): void => {
                      handleClose();
                      navigate('/pages/pof-policy');
                    }}
                  >
                    {t('deposit-safer-gambling-link-how-funds-are-protected')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </PopUpHandler>
      )}
      <FirstDepositConsents onClose={handleClose} cashierShowing={isOpen} show={showPoF} setShow={setShowPoF} />
    </>
  );
};
